import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ProgressBar } from 'primereact/progressbar';
import CadastroDataTable from '../../components/CadastroDataTable/CadastroDataTable';
import { richItemTemplate, richValueTemplate } from '../../components/RichSelectOption';
import '../../components/RichSelectOption.css';
import {
  buildAgenteFilterOptions,
  buildFilialFilterOptions,
  mesAtualRange,
  downloadFromApi,
  openPdfFromContent,
} from '../../utils/filtroHelpers';
import {
  toIsoDateLocal,
  loadFiltrosPersistidos,
  saveFiltrosPersistidos,
  filtrosTemDatas,
  buildExportFilterParams,
} from '../../utils/filtroPersistencia';
import api, { API_BASE_URL, normalize } from '../../services/api';
import './NfseConsulta.css';

const FILTROS_STORAGE_KEY = 'nfse-filtros:tomadas';

const STATUS_TOMADAS = [
  { label: 'Todos', value: '' },
  { label: 'Recebida', value: 'Recebida' },
  { label: 'Cancelada', value: 'Cancelada' },
  { label: 'Processando sync', value: 'ProcessandoSync' },
  { label: 'Erro sync', value: 'ErroSync' },
];

const FILTROS_OPCIONAIS = [
  { id: 'numero', label: 'Número NFS-e' },
  { id: 'chave', label: 'Chave de acesso' },
  { id: 'prestador', label: 'Prestador (texto)' },
  { id: 'codigoItemServico', label: 'Código item serviço' },
  { id: 'busca', label: 'Busca livre' },
];

const EMPTY_FILTROS = () => {
  const { dataInicio, dataFim } = mesAtualRange();
  return {
    dataInicio,
    dataFim,
    filialId: null,
    status: '',
    numero: '',
    chave: '',
    prestador: '',
    prestadorAgenteId: null,
    codigoItemServico: '',
    busca: '',
  };
};

function statusSeverity(status) {
  switch (status) {
    case 'Recebida':
      return 'success';
    case 'ProcessandoSync':
      return 'warning';
    case 'Cancelada':
    case 'ErroSync':
      return 'danger';
    default:
      return 'secondary';
  }
}

function loadFiltrosVisiveis() {
  try {
    const raw = localStorage.getItem('nfse-filtros-visiveis:tomadas');
    if (!raw) return FILTROS_OPCIONAIS.map((f) => f.id);
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : FILTROS_OPCIONAIS.map((f) => f.id);
  } catch {
    return FILTROS_OPCIONAIS.map((f) => f.id);
  }
}

function errMsg(e, fallback) {
  return e?.response?.data?.message || fallback;
}

function simNao(v) {
  if (v === true || v === 1 || v === '1' || v === 'S' || v === 's') return 'Sim';
  if (v === false || v === 0 || v === '0' || v === 'N' || v === 'n') return 'Não';
  return v == null || v === '' ? '—' : String(v);
}

export default function NfseTomadasPage() {
  const toast = useRef(null);
  const [filtrosAbertos, setFiltrosAbertos] = useState(true);
  const [filtros, setFiltros] = useState(() =>
    loadFiltrosPersistidos(FILTROS_STORAGE_KEY, EMPTY_FILTROS)
  );
  const [aplicados, setAplicados] = useState(() =>
    loadFiltrosPersistidos(FILTROS_STORAGE_KEY, EMPTY_FILTROS)
  );
  const [empresa, setEmpresa] = useState(null);
  const [filiais, setFiliais] = useState([]);
  const [prestadores, setPrestadores] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selection, setSelection] = useState([]);
  const [filtrosCfgVisible, setFiltrosCfgVisible] = useState(false);
  const [filtrosVisiveis, setFiltrosVisiveis] = useState(loadFiltrosVisiveis);
  const [draftVisiveis, setDraftVisiveis] = useState(loadFiltrosVisiveis);
  const [detalhe, setDetalhe] = useState(null);
  const [detalheVisible, setDetalheVisible] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [exportMsg, setExportMsg] = useState('');

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [eRes, fRes, aRes] = await Promise.all([
          api.get('/empresas/me').catch(() => ({ data: null })),
          api.get('/filiais').catch(() => ({ data: [] })),
          api.get('/agentes', { params: { tipo: 'Fornecedor' } }).catch(() => ({ data: [] })),
        ]);
        setEmpresa(normalize(eRes.data));
        setFiliais(normalize(fRes.data));
        setPrestadores(normalize(aRes.data));
      } catch {
        // lookups opcionais
      }
    };
    loadLookups();
  }, []);

  const carregar = useCallback(async (f = aplicados) => {
    setLoading(true);
    try {
      const params = {};
      if (f.dataInicio) params.dataInicio = toIsoDateLocal(f.dataInicio);
      if (f.dataFim) params.dataFim = toIsoDateLocal(f.dataFim);
      if (f.filialId != null) params.filialId = f.filialId;
      if (f.status) params.status = f.status;
      if (f.numero) params.numero = f.numero;
      if (f.chave) params.chave = f.chave;
      if (f.prestador) params.prestador = f.prestador;
      if (f.prestadorAgenteId != null) params.prestadorAgenteId = f.prestadorAgenteId;
      if (f.codigoItemServico) params.codigoItemServico = f.codigoItemServico;
      if (f.busca) params.busca = f.busca;
      const { data } = await api.get('/nfse/tomadas', { params });
      setRows(normalize(data));
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: errMsg(e, 'Falha ao carregar NFS-e tomadas.'),
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [aplicados]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const filialOptions = useMemo(
    () => buildFilialFilterOptions(empresa, filiais, { incluirTodas: true }),
    [empresa, filiais]
  );

  const prestadorOptions = useMemo(
    () =>
      buildAgenteFilterOptions(prestadores, {
        filialId: filtros.filialId,
        incluirTodos: true,
        todosLabel: 'Todos',
      }),
    [prestadores, filtros.filialId]
  );

  const setCampo = (campo, valor) =>
    setFiltros((p) => {
      const next = { ...p, [campo]: valor };
      if (campo === 'filialId') next.prestadorAgenteId = null;
      return next;
    });

  const aplicar = () => {
    const next = { ...filtros };
    setAplicados(next);
    setSelection([]);
    saveFiltrosPersistidos(FILTROS_STORAGE_KEY, next);
  };

  const limpar = () => {
    const empty = EMPTY_FILTROS();
    setFiltros(empty);
    setAplicados(empty);
    setSelection([]);
    saveFiltrosPersistidos(FILTROS_STORAGE_KEY, empty);
  };

  const money = (v) =>
    Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const idsSelecionados = () => (selection || []).map((r) => r.id).filter(Boolean);

  const sincronizar = async () => {
    setSyncing(true);
    toast.current?.show({
      severity: 'info',
      summary: 'Sincronização',
      detail:
        'Processo iniciado. Pode demorar vários minutos (ADN + consulta CNPJ dos agentes). Não feche a página; o status será atualizado ao terminar.',
      life: 10000,
    });
    try {
      const params = {};
      if (filtros.filialId != null) params.filialId = filtros.filialId;
      const { data } = await api.post('/nfse/tomadas/sincronizar', null, { params, timeout: 30000 });
      const r = normalize(data) || {};
      const iniciado = r.iniciado === true || r.emAndamento === true || r.EmAndamento === true;

      if (!iniciado && r.sucesso === false) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Atenção',
          detail: r.mensagem || r.Mensagem || 'Não foi possível iniciar a sincronização.',
          life: 8000,
        });
        setSyncing(false);
        return;
      }

      // Poll até sair de Executando (máx ~40 min)
      const deadline = Date.now() + 40 * 60 * 1000;
      let ultimoStatus = r;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 5000));
        try {
          const st = await api.get('/nfse/tomadas/sincronizar/status', { params, timeout: 15000 });
          ultimoStatus = normalize(st.data) || {};
          const emAndamento =
            ultimoStatus.emAndamento === true ||
            String(ultimoStatus.statusJob || '').toLowerCase() === 'executando';
          if (!emAndamento) break;
        } catch {
          // rede intermitente — continua tentando
        }
      }

      const statusFinal = String(ultimoStatus.statusJob || '').toLowerCase();
      const aindaRodando = statusFinal === 'executando' || ultimoStatus.emAndamento === true;
      const ok = !aindaRodando && statusFinal !== 'erro';
      const msg = aindaRodando
        ? 'Sincronização ainda em andamento no servidor. Atualize a lista mais tarde.'
        : ultimoStatus.mensagem ||
          (ok
            ? 'Sincronização concluída. Atualizando a lista…'
            : 'Sincronização finalizou com erro. Verifique o certificado/ADN.');

      toast.current?.show({
        severity: aindaRodando ? 'info' : ok ? 'success' : 'warn',
        summary: 'Sincronização',
        detail: msg,
        life: 10000,
      });

      try {
        const aRes = await api.get('/agentes', { params: { tipo: 'Fornecedor' } });
        setPrestadores(normalize(aRes.data));
      } catch {
        // ignore
      }
      carregar();
    } catch (e) {
      const isTimeout =
        e?.code === 'ECONNABORTED' ||
        /timeout/i.test(String(e?.message || '')) ||
        e?.response?.status === 504 ||
        e?.response?.status === 524;
      toast.current?.show({
        severity: isTimeout ? 'info' : 'error',
        summary: isTimeout ? 'Sincronização' : 'Erro',
        detail: isTimeout
          ? 'A requisição demorou, mas o processo pode continuar no servidor. Aguarde alguns minutos e atualize a lista.'
          : errMsg(e, e.message || 'Falha ao sincronizar ADN.'),
        life: 12000,
      });
      if (isTimeout) {
        try {
          carregar();
        } catch {
          // ignore
        }
      }
    } finally {
      setSyncing(false);
    }
  };

  /** ZIP/XML da seleção — exporta apenas as NFS-e marcadas na grid (por id). */
  const exportarSelecao = async (formato = 'zip') => {
    const ids = idsSelecionados();
    if (!ids.length) {
      toast.current?.show({ severity: 'warn', summary: 'Seleção', detail: 'Selecione ao menos uma NFS-e.' });
      return;
    }
    setExportando(true);
    setExportMsg(
      formato === 'zip'
        ? 'Gerando ZIP da seleção (XML + PDF). Isso pode levar alguns minutos…'
        : 'Exportando XML da seleção…'
    );
    try {
      await downloadFromApi(
        API_BASE_URL,
        'nfse/tomadas/exportar',
        { formato, ids: ids.join(',') },
        `nfse-tomadas.${formato === 'zip' ? 'zip' : formato === 'xml' ? 'xml' : 'csv'}`
      );
      toast.current?.show({ severity: 'success', summary: 'Export', detail: `Arquivo ${formato.toUpperCase()} gerado (seleção).` });
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Export', detail: errMsg(e, e.message || 'Falha ao exportar.') });
    } finally {
      setExportando(false);
      setExportMsg('');
    }
  };

  /** ZIP por filtros — exporta TODAS as NFS-e tomadas que casam com os filtros aplicados. */
  const exportarPorFiltros = async (formato = 'zip') => {
    if (!filtrosTemDatas(aplicados)) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Filtros',
        detail: 'Defina data início e data fim nos filtros para exportar por filtros.',
      });
      return;
    }
    setExportando(true);
    setExportMsg('Gerando ZIP por filtros (XML + PDF). Notas sem PDF em cache serão geradas agora — aguarde…');
    try {
      const params = buildExportFilterParams(aplicados, {
        agenteIdKey: 'prestadorAgenteId',
        agenteIdParam: 'prestadorAgenteId',
      });
      params.formato = formato;
      await downloadFromApi(API_BASE_URL, 'nfse/tomadas/exportar', params, 'nfse-tomadas.zip');
      toast.current?.show({ severity: 'success', summary: 'Export', detail: 'ZIP por filtros gerado.' });
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Export', detail: errMsg(e, e.message || 'Falha ao exportar.') });
    } finally {
      setExportando(false);
      setExportMsg('');
    }
  };

  const verXml = async (row) => {
    try {
      const res = await api.get(`/nfse/tomadas/${row.id}/xml`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'XML', detail: errMsg(e, 'XML não disponível.') });
    }
  };

  const verDanfse = async (row) => {
    try {
      const { data } = await api.get(`/nfse/tomadas/${row.id}/pdf`, { timeout: 120000 });
      const content = data?.content ?? data?.Content;
      if (!content) throw new Error('PDF vazio');
      openPdfFromContent(content, `danfse-${row.numeroNfse || row.id}.pdf`);
    } catch (e) {
      const label = `id=${row.id} nº=${row.numeroNfse || '-'} chave=${(row.chaveAcesso || '').slice(0, 24)}…`;
      toast.current?.show({
        severity: 'error',
        summary: 'DANFSe',
        detail: `${label} — ${errMsg(e, 'Não foi possível gerar o DANFSe (verifique XML/ACBr).')}`,
        life: 15000,
      });
    }
  };

  const verDetalhe = async (row) => {
    try {
      const { data } = await api.get(`/nfse/tomadas/${row.id}`);
      setDetalhe(normalize(data));
      setDetalheVisible(true);
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Detalhe', detail: errMsg(e, 'Falha ao carregar detalhe.') });
    }
  };

  const columns = [
    { field: 'numeroNfse', header: 'Nº NFS-e' },
    { field: 'prestadorNome', header: 'Prestador' },
    { field: 'prestadorCnpj', header: 'CNPJ prestador', defaultVisible: false },
    { field: 'tomadorNome', header: 'Tomador', defaultVisible: false },
    { field: 'filialNome', header: 'Empresa / Filial' },
    { field: 'competencia', header: 'Competência', style: { width: 110 } },
    {
      field: 'valorServicos',
      header: 'Valor serviços',
      body: (r) => money(r.valorServicos),
      exportValue: (r) => money(r.valorServicos),
      style: { width: 120 },
    },
    {
      field: 'valorIss',
      header: 'Valor ISS',
      body: (r) => money(r.valorIss),
      exportValue: (r) => money(r.valorIss),
      defaultVisible: false,
      style: { width: 110 },
    },
    {
      field: 'valorIbs',
      header: 'Valor IBS',
      body: (r) => money(r.valorIbs),
      exportValue: (r) => money(r.valorIbs),
      defaultVisible: false,
      style: { width: 110 },
    },
    {
      field: 'valorCbs',
      header: 'Valor CBS',
      body: (r) => money(r.valorCbs),
      exportValue: (r) => money(r.valorCbs),
      defaultVisible: false,
      style: { width: 110 },
    },
    {
      field: 'status',
      header: 'Status',
      body: (r) => (
        <Tag className="nfse-status-tag" severity={statusSeverity(r.status)} value={r.status} />
      ),
      exportValue: (r) => r.status,
      style: { width: 140 },
    },
    {
      field: 'dataEmissao',
      header: 'Emissão',
      body: (r) => (r.dataEmissao ? String(r.dataEmissao).slice(0, 10) : '—'),
      exportValue: (r) => (r.dataEmissao ? String(r.dataEmissao).slice(0, 10) : ''),
    },
    { field: 'codigoItemServico', header: 'Cód. item serviço', defaultVisible: false },
    { field: 'codigoNbs', header: 'Código NBS', defaultVisible: false },
    { field: 'indOp', header: 'Ind. operação', defaultVisible: false },
    { field: 'localPrestacaoIbge', header: 'Local prestação (IBGE)', defaultVisible: false },
    {
      field: 'issRetido',
      header: 'ISS retido',
      body: (r) => simNao(r.issRetido),
      exportValue: (r) => simNao(r.issRetido),
      defaultVisible: false,
      style: { width: 100 },
    },
    { field: 'discriminacao', header: 'Discriminação', defaultVisible: false },
    { field: 'chaveAcesso', header: 'Chave de acesso', defaultVisible: false },
    { field: 'nsu', header: 'NSU', style: { width: 100 } },
    { field: 'ambiente', header: 'Ambiente', defaultVisible: false },
    {
      field: 'temXml',
      header: 'Tem XML',
      body: (r) => simNao(r.temXml),
      exportValue: (r) => simNao(r.temXml),
      defaultVisible: false,
      style: { width: 90 },
    },
    {
      field: '_acoes',
      header: 'Ações',
      sortable: false,
      alwaysVisible: true,
      style: { width: 160 },
      body: (row) => (
        <div className="nfse-row-actions">
          <Button icon="pi pi-eye" rounded text tooltip="Detalhe" onClick={() => verDetalhe(row)} />
          <Button icon="pi pi-file-pdf" rounded text tooltip="DANFSe" onClick={() => verDanfse(row)} disabled={!row.temXml} />
          <Button icon="pi pi-code" rounded text tooltip="XML" onClick={() => verXml(row)} disabled={!row.temXml} />
        </div>
      ),
    },
  ];

  const salvarFiltrosCfg = () => {
    setFiltrosVisiveis(draftVisiveis);
    try {
      localStorage.setItem('nfse-filtros-visiveis:tomadas', JSON.stringify(draftVisiveis));
    } catch {
      // ignore
    }
    setFiltrosCfgVisible(false);
  };

  return (
    <div className="nfse-consulta-page">
      <Toast ref={toast} />

      <Dialog
        header="Exportando XML + PDF"
        visible={exportando}
        closable={false}
        modal
        style={{ width: 420 }}
        onHide={() => {}}
      >
        <p style={{ marginTop: 0 }}>{exportMsg || 'Processando…'}</p>
        <ProgressBar mode="indeterminate" style={{ height: 10 }} />
        <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 0 }}>
          Não feche esta página até o download iniciar.
        </p>
      </Dialog>

      <div className="nfse-consulta-header">
        <h1>NFSe Tomadas</h1>
        <div className="nfse-consulta-header-actions">
          <Button
            label="ZIP por filtros (XML+PDF)"
            icon="pi pi-filter"
            className="p-button-outlined"
            disabled={!filtrosTemDatas(aplicados) || exportando}
            loading={exportando}
            onClick={() => exportarPorFiltros('zip')}
            tooltip="Exporta XML+PDF de TODAS as NFS-e tomadas que casam com os filtros aplicados (todas as páginas), não só a seleção atual. Exige período (data início/fim) definido."
            tooltipOptions={{ position: 'bottom' }}
          />
          <Button
            label={syncing ? 'Sincronizando…' : 'Sincronizar'}
            icon="pi pi-sync"
            className="p-button-outlined"
            loading={syncing}
            disabled={syncing}
            onClick={sincronizar}
            tooltip={
              syncing
                ? 'Sincronização ADN em andamento — pode demorar vários minutos'
                : 'Baixa NFS-e destinadas do ADN (pode demorar)'
            }
            tooltipOptions={{ position: 'bottom' }}
          />
        </div>
        <p className="nfse-consulta-header-hint">
          <strong>ZIP por filtros</strong> exporta todas as notas do período/filtros atuais
          (todas as páginas). <strong>ZIP da seleção</strong> (na barra abaixo da grid) exporta só
          as notas marcadas na tabela.
        </p>
      </div>

      <div className="nfse-filtros-card">
        <div className="nfse-filtros-card-head">
          <h2>Filtros</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              label="Configurar filtros"
              icon="pi pi-sliders-h"
              className="p-button-text p-button-sm nfse-filtros-toggle"
              onClick={() => {
                setDraftVisiveis(filtrosVisiveis);
                setFiltrosCfgVisible(true);
              }}
            />
            <Button
              label={filtrosAbertos ? 'Ocultar' : 'Mostrar'}
              icon={filtrosAbertos ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}
              className="p-button-text p-button-sm nfse-filtros-toggle"
              onClick={() => setFiltrosAbertos((v) => !v)}
            />
          </div>
        </div>

        {filtrosAbertos && (
          <div className="nfse-filtros-grid">
            <div className="nfse-filtro-field">
              <label>Data início</label>
              <Calendar
                value={filtros.dataInicio}
                onChange={(e) => setCampo('dataInicio', e.value)}
                dateFormat="dd/mm/yy"
                locale="pt"
                showIcon
                icon="pi pi-calendar"
                placeholder="Início"
              />
            </div>
            <div className="nfse-filtro-field">
              <label>Data fim</label>
              <Calendar
                value={filtros.dataFim}
                onChange={(e) => setCampo('dataFim', e.value)}
                dateFormat="dd/mm/yy"
                locale="pt"
                showIcon
                icon="pi pi-calendar"
                placeholder="Fim"
              />
            </div>
            <div className="nfse-filtro-field nfse-filtro-field-xl">
              <label>Empresa / Filial</label>
              <Dropdown
                value={filtros.filialId}
                options={filialOptions}
                onChange={(e) => setCampo('filialId', e.value)}
                placeholder="Todas"
                filter
                filterBy="search,label,razao,fantasia,cnpjcpf"
                itemTemplate={richItemTemplate}
                valueTemplate={richValueTemplate}
                panelClassName="rich-select-panel"
                optionLabel="label"
              />
            </div>
            <div className="nfse-filtro-field nfse-filtro-field-xl">
              <label>Prestador (cadastro)</label>
              <Dropdown
                value={filtros.prestadorAgenteId}
                options={prestadorOptions}
                onChange={(e) => setCampo('prestadorAgenteId', e.value)}
                placeholder="Todos"
                filter
                filterBy="search,label,razao,fantasia,cnpjcpf"
                itemTemplate={richItemTemplate}
                valueTemplate={richValueTemplate}
                panelClassName="rich-select-panel"
                optionLabel="label"
                showClear
                emptyMessage="Nenhum fornecedor nesta filial"
              />
            </div>
            <div className="nfse-filtro-field">
              <label>Status</label>
              <Dropdown
                value={filtros.status}
                options={STATUS_TOMADAS}
                onChange={(e) => setCampo('status', e.value)}
              />
            </div>
            {filtrosVisiveis.includes('numero') && (
              <div className="nfse-filtro-field">
                <label>Número NFS-e</label>
                <input
                  className="p-inputtext p-component"
                  value={filtros.numero}
                  onChange={(e) => setCampo('numero', e.target.value)}
                />
              </div>
            )}
            {filtrosVisiveis.includes('chave') && (
              <div className="nfse-filtro-field nfse-filtro-field-wide">
                <label>Chave de acesso</label>
                <input
                  className="p-inputtext p-component"
                  value={filtros.chave}
                  onChange={(e) => setCampo('chave', e.target.value)}
                />
              </div>
            )}
            {filtrosVisiveis.includes('prestador') && (
              <div className="nfse-filtro-field nfse-filtro-field-wide">
                <label>Prestador (texto)</label>
                <input
                  className="p-inputtext p-component"
                  value={filtros.prestador}
                  onChange={(e) => setCampo('prestador', e.target.value)}
                  placeholder="Nome ou CNPJ"
                />
              </div>
            )}
            {filtrosVisiveis.includes('codigoItemServico') && (
              <div className="nfse-filtro-field">
                <label>Código item serviço</label>
                <input
                  className="p-inputtext p-component"
                  value={filtros.codigoItemServico}
                  onChange={(e) => setCampo('codigoItemServico', e.target.value)}
                />
              </div>
            )}
            {filtrosVisiveis.includes('busca') && (
              <div className="nfse-filtro-field nfse-filtro-field-wide">
                <label>Busca livre</label>
                <input
                  className="p-inputtext p-component"
                  value={filtros.busca}
                  onChange={(e) => setCampo('busca', e.target.value)}
                  placeholder="Texto livre"
                />
              </div>
            )}
            <div className="nfse-filtros-actions">
              <Button label="Limpar" className="p-button-text" onClick={limpar} />
              <Button label="Aplicar" icon="pi pi-filter" onClick={aplicar} />
            </div>
          </div>
        )}
      </div>

      {selection?.length > 0 && (
        <div className="nfse-lote-bar">
          <strong>{selection.length}</strong> selecionada(s)
          <Button
            label="ZIP da seleção"
            icon="pi pi-file"
            className="p-button-sm p-button-outlined"
            loading={exportando}
            disabled={exportando}
            onClick={() => exportarSelecao('zip')}
            tooltip="Exporta XML+PDF apenas das NFS-e marcadas nesta grid"
          />
          <Button
            label="XML"
            icon="pi pi-code"
            className="p-button-sm p-button-outlined"
            disabled={exportando}
            onClick={() => exportarSelecao('xml')}
          />
          {selection.length === 1 && (
            <>
              <Button
                label="DANFSe"
                icon="pi pi-file-pdf"
                className="p-button-sm p-button-outlined"
                onClick={() => verDanfse(selection[0])}
              />
              <Button
                label="Ver XML"
                icon="pi pi-eye"
                className="p-button-sm p-button-outlined"
                onClick={() => verXml(selection[0])}
              />
            </>
          )}
        </div>
      )}

      <CadastroDataTable
        value={rows}
        columns={columns}
        storageKey="nfse-tomadas"
        loading={loading}
        title="NFSe Tomadas"
        emptyMessage="Nenhuma NFS-e tomada encontrada com os filtros atuais."
        globalFilterFields={[
          'numeroNfse',
          'prestadorNome',
          'prestadorCnpj',
          'tomadorNome',
          'filialNome',
          'status',
          'chaveAcesso',
          'codigoItemServico',
          'discriminacao',
        ]}
        selectionMode="checkbox"
        selection={selection}
        onSelectionChange={(e) => setSelection(e.value || [])}
        rows={50}
        rowsPerPageOptions={[25, 50, 100, 250]}
      />

      <Dialog
        header="Configurar filtros"
        visible={filtrosCfgVisible}
        onHide={() => setFiltrosCfgVisible(false)}
        style={{ width: 420 }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Cancelar" className="p-button-text" onClick={() => setFiltrosCfgVisible(false)} />
            <Button label="Salvar" icon="pi pi-check" onClick={salvarFiltrosCfg} />
          </div>
        }
      >
        <p style={{ color: '#64748b', marginTop: 0 }}>
          Período, Empresa/Filial e Status são fixos. Marque os filtros adicionais:
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {FILTROS_OPCIONAIS.map((f) => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Checkbox
                inputId={`tflt-${f.id}`}
                checked={draftVisiveis.includes(f.id)}
                onChange={(e) => {
                  setDraftVisiveis((prev) =>
                    e.checked ? [...prev, f.id] : prev.filter((x) => x !== f.id)
                  );
                }}
              />
              <label htmlFor={`tflt-${f.id}`} style={{ cursor: 'pointer', margin: 0 }}>
                {f.label}
              </label>
            </div>
          ))}
        </div>
      </Dialog>

      <Dialog
        header={detalhe ? `NFS-e ${detalhe.numeroNfse || detalhe.id}` : 'Detalhe'}
        visible={detalheVisible}
        onHide={() => setDetalheVisible(false)}
        style={{ width: 'min(720px, 96vw)' }}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {detalhe?.temXml && (
              <>
                <Button label="DANFSe" icon="pi pi-file-pdf" className="p-button-outlined" onClick={() => verDanfse(detalhe)} />
                <Button label="XML" icon="pi pi-code" className="p-button-outlined" onClick={() => verXml(detalhe)} />
              </>
            )}
            <Button label="Fechar" onClick={() => setDetalheVisible(false)} />
          </div>
        }
      >
        {detalhe && (
          <div className="nfse-detalhe-grid">
            <div><strong>Prestador</strong><br />{detalhe.prestadorNome}<br />{detalhe.prestadorCnpj}</div>
            <div><strong>Tomador</strong><br />{detalhe.tomadorNome || '—'}</div>
            <div><strong>Status</strong><br />{detalhe.status}</div>
            <div><strong>Competência</strong><br />{detalhe.competencia}</div>
            <div><strong>Valor serviços</strong><br />{money(detalhe.valorServicos)}</div>
            <div><strong>ISS / alíq.</strong><br />{money(detalhe.valorIss)} ({detalhe.aliquotaIss ?? 0}%)</div>
            <div><strong>Item serviço</strong><br />{detalhe.codigoItemServico || '—'}</div>
            <div><strong>Trib. municipal</strong><br />{detalhe.codigoTributacaoMunicipal || '—'}</div>
            <div><strong>NBS</strong><br />{detalhe.codigoNbs || '—'}</div>
            <div><strong>Local prest. IBGE</strong><br />{detalhe.localPrestacaoIbge || '—'}</div>
            <div><strong>Chave</strong><br /><code style={{ wordBreak: 'break-all' }}>{detalhe.chaveAcesso || '—'}</code></div>
            <div style={{ gridColumn: '1 / -1' }}><strong>Discriminação</strong><br />{detalhe.discriminacao || '—'}</div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
